import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

const AdminThemeContext = createContext({ theme: 'dark', setTheme: () => {}, toggleTheme: () => {} });

const STORAGE_KEY = 'admin_theme';

export const AdminThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'light' || stored === 'dark' ? stored : 'dark';
    } catch (_e) {
      return 'dark';
    }
  });

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_e) { /* ignore */ }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem(STORAGE_KEY, next); } catch (_e) { /* ignore */ }
      return next;
    });
  }, []);

  // Apply the dark class on a stable root element so Tailwind `dark:` modifiers work
  // across the whole admin subtree (and any portals like dialog/popover).
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    return () => {
      // Strip on unmount so other (non-admin) routes are unaffected
      root.classList.remove('dark');
    };
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);
  return <AdminThemeContext.Provider value={value}>{children}</AdminThemeContext.Provider>;
};

export const useAdminTheme = () => useContext(AdminThemeContext);

export default AdminThemeContext;
